import { Component, OnInit, ViewChildren, QueryList, Inject, OnDestroy, ChangeDetectorRef, AfterViewInit, DOCUMENT } from '@angular/core';
import { FiltersService } from 'src/app/core/services/filters.service';
import { Subscription, combineLatest, skip, firstValueFrom, filter, timeout } from 'rxjs';
import { availablePrograms, custTile, eroiIds, ModalActions, tvdData, tvdGroupIds, TvdImpactCategory, TVDMetricGroupResponse, TvdProgramToGroupIdMapping, TvdValuePillar, TvdValuePillarToEroiIdMapping } from './types/tvd-types';
import { TVDService } from 'src/app/features/total-value-delivery/services/tvd.service';
import { Router } from '@angular/router';
import { AlertService } from 'src/app/core/services/alert.service';
import { TVDModel } from './types/tvd-model';
import { eroiService } from './services/eroi-metric.service';
import { MixpanelService } from "../../core/services/mixpanel.service";
import { EroiMetricComponent } from './components/eroi-metric/eroi-metric.component';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { TvdCostAssumptionsService } from './services/tvd-cost-assumptions.service';
import { CurrencyUomService, UomOption } from 'src/app/core/services/currency-uom.service';
import { Division, Region } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { CostAssumptionsEligibilityResolver } from './resolvers/cost-assumptions-eligibility.resolver';
import { InfoTooltip } from 'src/app/core/types/tooltip-types';
import {
  BusinessOutcomesTilesConfig,
  CompositeTileConfig,
  EnvironmentalImpactCompositeTilesConfig,
  EnvironmentalImpactTilesConfig,
  EroiTileConfig,
  Guard,
  OperationalPerformanceCompositeTilesConfig,
  OperationalPerformanceTilesConfig,
  TvdGuardFlag,
  compositeTilesConfig,
  tvdSummaryPillsConfig,
} from './types/tvd-metric-tiles-config';

@Component({
    selector: 'app-total-value-delivery',
    templateUrl: './total-value-delivery.component.html',
    styleUrls: ['./total-value-delivery.component.scss', 'styles/tvd-repeat-styles.scss'],
    standalone: false
})

export class TotalValueDeliveryComponent implements OnInit, OnDestroy, AfterViewInit {
  filters: ContractModel | undefined;
  private filtersSubscription: Subscription | undefined;
  private currencySubscription: Subscription | undefined;
  customTilesSubscription: Subscription | undefined;
  tvdModel: TVDModel | undefined;
  tvdSubscription: Subscription | undefined;
  private totalTvdSubscription: Subscription | undefined;

  tvdResponse: TVDMetricGroupResponse[] = [];

  makingSelectionsState: boolean = false;
  selectionConfirmationState: boolean = false;
  selectionsPreviewState: boolean = false;
  // Snapshot of isAddedToPres values taken when the selection panel opens,
  // so Cancel can restore the full committed state even if the preview was cleared mid-session.
  private _isAddedToPresSnapshot: Map<string, boolean> = new Map();

  pageTitle : string = "Total Value Delivery";

  coverVisible: boolean = false;
  custTileEntryVisible: boolean = false;
  custTileEditVisible: boolean = false;

  /** Denotes if only the EU Region is selected.
   * Used to determine which greenhouse gas metrics  to display */
  isRegionEU: boolean = false;

  isPestDivision: boolean = false;
  isAPInstitutional: boolean = false; // Used to determine if the division is AP Institutional for Green gas energy metrics
  isLAInstitutional: boolean = false;
  isIMEAInstitutional: boolean = false;

  //this gets passed into eroi-metric components, and they update it using their assigned string id.
  selectedItemsDict: Map<string, [data:tvdData, isSelected:boolean, isAddedToPres:boolean]> = new Map();

  customSelected: boolean = false;
  businessOutcomesSelected: boolean = false;
  operationalPerformanceSelected: boolean = false;
  environmentalImpactSelected: boolean = false;
  currUrl: string = '';
  isDrawerOpen = false;

  // programs
  tvdProgramScope: string = availablePrograms.ALL; //changes depending on which program tab is selected
  availableProgramTabs = Object.values(availablePrograms);
  availablePrograms = availablePrograms; // expose enum to html

  //expose enums for html
  TvdValuePillar = TvdValuePillar;
  TvdImpactCategory = TvdImpactCategory;

  //the types may need to change
  //These only affect the view. They are updated when the user navigates to different program tabs.
  tvdOverallData: tvdData = { monetaryContribution: '', valueContribution: '' };

  // Product TVD data
  waterData: tvdData = { monetaryContribution: '', valueContribution: '' };
  energyData: tvdData = { monetaryContribution: '', valueContribution: '' };
  greenhouseGasData: tvdData = { monetaryContribution: '', valueContribution: '' };
  greenhouseGasEnergyData: tvdData = { monetaryContribution: '', valueContribution: '' };
  greenhouseGasTransportData: tvdData = { monetaryContribution: '', valueContribution: '' };
  greenhouseGasPackagingData: tvdData = { monetaryContribution: '', valueContribution: '' };
  plasticWasteData: tvdData = { monetaryContribution: '', valueContribution: '' };
  cardboardWasteData: tvdData = { monetaryContribution: '', valueContribution: '' };
  employeeSafetyData: tvdData = { monetaryContribution: '', valueContribution: '' };
  productivityData: tvdData = { monetaryContribution: '', valueContribution: '' };
  assetsData: tvdData = { monetaryContribution: '', valueContribution: '' };
  costAvoidData: tvdData = { monetaryContribution: '', valueContribution: '' };
  portfolioData: tvdData = { monetaryContribution: '', valueContribution: '' };
  totalSales: string = '';

  // Services TVD data
  totalServices: string = '';
  noChargeAdditionalServicesData : tvdData = { monetaryContribution: '', valueContribution: '' };
  generalContractorCostAvoidanceData : tvdData = { monetaryContribution: '', valueContribution: '' };
  setupServicesData : tvdData = { monetaryContribution: '', valueContribution: '' };
  pestPreventionFindingsData : tvdData = { monetaryContribution: '', valueContribution: '' };
  roomNightsSavedData : tvdData = { monetaryContribution: '', valueContribution: '' };
  furniturePreservationAssetsData : tvdData = { monetaryContribution: '', valueContribution: '' };
  furniturePreservationEnergyData : tvdData = { monetaryContribution: '', valueContribution: '' };

  isMetricSelected: boolean = false;

  isInitialized: boolean = false;

  // maximum custom tiles
  maxCustomTiles: number = 9;

  // max number of tiles allowed in the presentation, including both custom tiles and regular TVD metrics.
  // This is to prevent overcrowding of the presentation slide.
  maxTilesPerValuePillar: number = 8;

  //this keeps track of custom tiles, by their title, passed into metric-confirmation.component and custom-tile-entry
  customTilesDict: Map<string, custTile> | undefined = new Map();

  custTileSectionVisible: boolean | undefined;

  selectedMetrics : string[] = [];

  //Direct access to the service's dictionary
  serviceCustomTileDict : Map<string, custTile> | undefined;
  costCurrencyCode: string = "USD";
  isCostAssumptionsVisible: boolean = false;



  // To check if the filter selections are for multi-region/global TVD
  isMultipleRegionsSelected: boolean = false;

  private displayInRoundedNumberSubscription: Subscription | undefined;
  private showInEnterpriseTermsSubscription: Subscription | undefined;
  private loadingOverlaySubscription: Subscription | undefined;
  isInitialTvdLoadCompletedObservable: boolean = false;
  showLoadingOverlay: boolean = false;
  displayInRoundedNumber: boolean = true;
  showInEnterpriseTerms: boolean = true;
  measurementSystem: UomOption = 'Imperial';
  summaryPillMetrics: string[] = [eroiIds.WATER, eroiIds.ENERGY, eroiIds.SOLID_WASTE];
  valuePillars = Object.values(TvdValuePillar); // to preserve the order they are declared in the enum
  expandedValuePillarId: TvdValuePillar | null = null;
  infoTooltipType = InfoTooltip; // Expose enum to template

  operationalPerformanceChildren: EroiMetricComponent[] = [];
  environmentalImpactChildren: EroiMetricComponent[] = [];
  businessOutcomesChildren: EroiMetricComponent[] = [];

    //@ViewChildren: captures all EroiMetricComponent instances rendered by the
  // config-driven *ngFor loops in the refreshed UI, replacing individual @ViewChild refs.
  @ViewChildren(EroiMetricComponent) eroiMetricComponents!: QueryList<EroiMetricComponent>;
  /** Lookup map populated by rebuildEroiMetricRegistry(); keyed by metricId string value. */
  eroiComponentsByMetricId: Map<string, EroiMetricComponent> = new Map();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _filterService: FiltersService,
    private _alertService: AlertService,
    private _tvdService: TVDService,
    private _eroiService: eroiService,
    private _router: Router,
    private mixpanelService: MixpanelService,
    private _tvdCostAssumptionsService: TvdCostAssumptionsService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags,
    private _currencyUomService: CurrencyUomService,
    private _cdr: ChangeDetectorRef,
    private _uiPreferencesService: UIPreferencesService,
    private _costAssumptionsEligibilityResolver: CostAssumptionsEligibilityResolver
  ) {
    this.currUrl = this.document.location.href;
    this.serviceCustomTileDict = _tvdService.custTileDict;
  }

  get hasTvdByPrograms() {
    return this.filters?.division != Division.Distributor && this.filters?.division != Division.Pest;
  }

  private updateTvdItems() {
    if (this.tvdModel != undefined && this.tvdModel.tvdResponse != undefined) {
      this.tvdResponse = this.tvdModel.tvdResponse;
      this.customTilesDict = this.tvdModel.custTileDict;

      if(this.customTilesDict){ //if not undefined
        if (this.customTilesDict.size > 0) {
          this.showCustTileSection();
        }
      } else {
        this.customTilesDict = new Map();
        this.hideCustTileSection();
      }

      this._parseData();
      this._setSelectedItemsDict(); // Set the selectedItemsDict once data is available instead of default values
      this.sendData();

    }
    else {
      if (this.isInitialized) {
        this._alertService.addAlert({
          message: "TVD Data Model is undefined, please try again. If issue persists, report a bug.",
          id: Math.random() * 25
        });
      }
    }
  }

  async ngOnInit() {
    this.filtersSubscription = this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.displayInRoundedNumberSubscription = this._uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayInRoundedNumber = f);
    this.showInEnterpriseTermsSubscription = this._tvdService.showInEnterpriseTermsObservable.subscribe(f => this.showInEnterpriseTerms = f);
    this.loadingOverlaySubscription = this._tvdService.showLoadingOverlayObservable.subscribe(loading => this.showLoadingOverlay = loading);
    this.tvdSubscription = this._tvdService.tvdObservable.subscribe(t => {
      this.tvdModel = t;
      this._tvdService.recalculateTvdTotals(this.tvdModel, this.customTilesDict);
      this.updateTvdItems();
    });

    this.totalTvdSubscription = this._tvdService.totalTotalValueDeliveredObservable.subscribe(data => {
      if (!data) {
        return;
      }

      this.tvdOverallData = {
        monetaryContribution: data.monetaryContribution,
        modifiedMonetaryContribution: data.modifiedMonetaryContribution,
        valueContribution: data.valueContribution,
        modifiedValueContribution: data.modifiedValueContribution
      };
    });

    if (this.filters?.regions != undefined) {

      const { regions, division } = this.filters;
      this.isAPInstitutional = regions.includes(Region.AP) && division === Division.Institutional;
      this.isLAInstitutional = regions.includes(Region.LA) && division === Division.Institutional;
      this.isIMEAInstitutional = regions.includes(Region.IMEA) && division === Division.Institutional;

      this.isPestDivision = this.filters?.division == Division.Pest;
      this.isMultipleRegionsSelected = this.filters?.regions.length > 1;
      this.isRegionEU = this.filters?.regions.every(region => region === Region.EU);

      this.isCostAssumptionsVisible = this._costAssumptionsEligibilityResolver.resolveFromFilters(this.filters);


      //Set Division to be used in service for groupIds
      this._tvdService.setDivision(this.filters?.division || '');
      this.costCurrencyCode = this._currencyUomService.getDisplayedCurrency();

      this.currencySubscription = combineLatest([
        this._currencyUomService.selectedCurrency$,
        this._currencyUomService.selectedUom$,
      ]).pipe(skip(1)).subscribe(async () => {
        // Display loading animations  while we fetch new data and update the metrics
        this._tvdService.setShowLoadingOverlay(true);
        this._currencyUomService.setCurrencyLoading(true);
        // Re-fetch TVD data from API for the new currency
        // Fetch conversion rate from API and apply it to the cost of each custom tile
        const [conversionRate, refresh] = await Promise.all([
          this._currencyUomService.getCurrencyConversionRate(),
          this._tvdService.refreshTVDDataAfterCurrencyChange()
        ]);

        //Before cost assumptions can be applied, need to reset the original monetary contributions
        this._tvdService.resetOriginalMonetaryContributions();

        // Update custom tiles costs based on new currency
        // We update the custom tiles here instead of within their own component
        // because we want to make sure all custom tiles are updated at the same time.
        // Also we want to ensure the cost of the custom tiles are updated before
        // the tvd metrics added to the Powerpoint downlaod request model is updated.
        if(this.customTilesDict){
          //Update the custom tiles while applying the conversion rate
          this.customTilesDict.forEach((value, key) => {
            // Remove commas from cost string, convert to number,
            // apply conversion rate, and convert back to string
            value.cost = Math.round(Number(value.cost.replace(/,/g, '')) * conversionRate).toLocaleString();
          });
          this._tvdService.updateCustomTileDict(this.customTilesDict!);

          //Update the cost of the custom tiles in the presentation model in TVD service,
          //which is used for PPT download request payload
          this._tvdService.updateAddedMetrics([]);
          this.customTilesDict.forEach((value, key) => {
            this._tvdService.addCustomMetricToPresentation(value.title)
          });

          // update PPT download request payload
          this._tvdService.confirmDataUpdate();
        }

        // Get the user selections from session storage.
        // If the user custom picked metrics to be added to the presentation,
        // we want to make sure those selections are not lost when the currency
        // change triggers a refresh of the TVD data.
        // If user did not make any selections, then this.selectedItemsDict.size would be zero.
        const selectedItemsDictSessionSize = this.getMetricsFromSessionStorage();
        if(selectedItemsDictSessionSize > 0){
          this._tvdService.updateSelectionsPayload(this.selectedItemsDict);
        }

        this._tvdService.setShowLoadingOverlay(false);
        this._currencyUomService.setCurrencyLoading(false);
        this.costCurrencyCode = this._currencyUomService.getDisplayedCurrency();
        this.measurementSystem = this._currencyUomService.getUom();
      });

      // Set max custom tiles
      this.maxCustomTiles = 9;

      //set selected program to 'All' on init
      this._tvdService.setTvdProgramSelected(this.tvdProgramScope)

      const divisionsWithoutProgramTabs = ['QSR', 'FRS', 'Pest'];
      //If the selected filters does not include the Institutional, NA region, then limit the program tabs visible
      if(divisionsWithoutProgramTabs.includes(this.filters?.division) || !this.filters?.regions.includes('NA'))
        this.limitProgramTabs(); // limit the program tabs to only ALL

      this.customTilesSubscription = this._tvdService.customTilesObservable.subscribe(async (c) => {
        this.customTilesDict = c;
        this.refreshValuePillarSelectionStates();
        if(this.customTilesDict){ //if not undefined
          // Recalculate when custom tiles change and there are cost adjustments
          this._tvdService.recalculateTvdTotals(this.tvdModel, this.customTilesDict);
          // If no cost adjustments, addCustomTotal() already handles it
        }
      });

      //Get the metrics added to the presentation from session storage
      this.getMetricsFromSessionStorage();
      if (this.tvdModel == undefined || this.tvdModel?.tvdResponse.length == 0) {
        await this._tvdService.setToDefaultTVDModel();
      }

      // Push metrics to mixpanel.
      // When only H7s are selected, inferredH5 is fetched asynchronously after navigation.
      // Wait for it to arrive (up to 5s) so the event captures the correct H5 values.
      // TODO: We can think of using a subscription here instead of the 5s delay.
      const isContractLikeDivision =
        this.filters.division === 'QSR' ||
        this.filters.division === 'FRS' ||
        (this.filters.division === 'Institutional' && this.filters.dataView === 'Contract');
      const needsInferredH5 = isContractLikeDivision &&
        !this.filters.topLevelContractGroup?.length &&
        !!this.filters.secondLevelContractFilter?.length;

      if (needsInferredH5) {
        firstValueFrom(
          this._filterService.selectedFiltersObservable.pipe(
            filter(f => !!f?.inferredH5),
            timeout(5000)
          )
        ).then(filtersWithH5 => {
          this.mixpanelService.trackEventWithFilters('TVD Dashboard Loaded', filtersWithH5, this.pageTitle);
        }).catch(() => {
          // Timeout or lookup failed — track with current filters
          this.mixpanelService.trackEventWithFilters('TVD Dashboard Loaded', this.filters, this.pageTitle);
        });
      } else {
        this.mixpanelService.trackEventWithFilters('TVD Dashboard Loaded', this.filters, this.pageTitle);
      }
      this.mixpanelService.initializeActiveDurationTracking(); // start active duration tracking

      this._tvdService.tvdModalButtonAction$.subscribe(action => {
        if(action != undefined && action != ''){
          this.getModalAction(action);
        }
      });

    }
    else{
      // TODO: What's the history behind this localhost check? It seems like a workaround, but it's not clear what issue it's addressing. We should investigate and find a more robust solution if possible.
      if (this.currUrl.lastIndexOf('localhost') < 0) {
        alert('Please apply filters before navigating to Total Value Delivery')
        // TODO: This navigation should ideally be handled by a route guard instead of within the component. We should consider refactoring this logic into a guard to prevent unauthorized access to the TVD page.
        this._router.navigate(['/filter']);
      }

      // TODO: If it reaches this point, it means the filters are not set, which is a critical issue for the TVD page. We should consider implementing a more robust error handling mechanism here, such as displaying an error message to the user and providing guidance on how to set the filters correctly.
      this.isCostAssumptionsVisible = false;

      // TODO: Why is there a need to mark it as initialized here when the filters are not set? This seems counterintuitive and could lead to issues down the line. We should investigate the implications of this and consider whether it's necessary or if there's a better way to handle the uninitialized state of the component when filters are not set.
      this.isInitialized = true;
    }
    this.updateIsMetricsSelectedFunc();


    //emits change for customTilesDict
    this._tvdService.updateCustomTileDict(this.customTilesDict!);
    this._tvdService.setTvdInitialLoadComplete(true);
    this.measurementSystem = this._currencyUomService.getUom();
  }

  ngOnDestroy() {
    this.filtersSubscription?.unsubscribe();
    this.currencySubscription?.unsubscribe();
    this.loadingOverlaySubscription?.unsubscribe();
    this.mixpanelService.stopTrackingActiveTime();
    this.mixpanelService.trackPageView(this.pageTitle); //send page view event, which includes Page Name and Active Duration
    this.displayInRoundedNumberSubscription?.unsubscribe();
    this.showInEnterpriseTermsSubscription?.unsubscribe();
    this.totalTvdSubscription?.unsubscribe();
  }


  /**
   * After the view initialises, build the eroi component registry and subscribe
   * to QueryList.changes so the registry stays in sync whenever the *ngFor-driven tile
   * list changes (e.g. program tab switch, data update).
   */
  ngAfterViewInit(): void {
    this.rebuildEroiMetricRegistry();
    this.eroiMetricComponents.changes.subscribe(() => this.rebuildEroiMetricRegistry());
  }

  /**
   * Rebuilds the metricId → EroiMetricComponent lookup map from the @ViewChildren QueryList
   * and repopulates the three pillar-children arrays used by selection / toggle logic.
   */
  private rebuildEroiMetricRegistry(): void {
    this.eroiComponentsByMetricId.clear();
    this.eroiMetricComponents.forEach(component => {
      if (component.metricId) {
        this.eroiComponentsByMetricId.set(component.metricId, component);
      }
    });

    const ctx = this.getGuardContext();

    this.businessOutcomesChildren = BusinessOutcomesTilesConfig
      .filter(tile => this.passesGuard(tile.guard, ctx))
      .map(tile => this.eroiComponentsByMetricId.get(tile.metricId))
      .filter((c): c is EroiMetricComponent => !!c);

    this.operationalPerformanceChildren = [
      ...OperationalPerformanceTilesConfig
        .filter(tile => this.passesGuard(tile.guard, ctx))
        .map(tile => this.eroiComponentsByMetricId.get(tile.metricId))
        .filter((c): c is EroiMetricComponent => !!c),
      ...OperationalPerformanceCompositeTilesConfig
        .filter(composite => this.passesGuard(composite.guard, ctx))
        .flatMap(composite => composite.children)
        .map(child => this.eroiComponentsByMetricId.get(child.metricId))
        .filter((c): c is EroiMetricComponent => !!c),
    ];

    this.environmentalImpactChildren = [
      ...EnvironmentalImpactTilesConfig
        .filter(tile => this.passesGuard(tile.guard, ctx))
        .map(tile => this.eroiComponentsByMetricId.get(tile.metricId))
        .filter((c): c is EroiMetricComponent => !!c),
      ...EnvironmentalImpactCompositeTilesConfig
        .filter(composite => this.passesGuard(composite.guard, ctx))
        .flatMap(composite => composite.children)
        .map(child => this.eroiComponentsByMetricId.get(child.metricId))
        .filter((c): c is EroiMetricComponent => !!c),
    ];

    this.syncCostAssumptionsMetricRefs();
  }

  /**
   * Pushes all currently-rendered EroiMetricComponent instances into the TVD service
   * so cost-assumptions calculations stay in sync with the dynamically rendered tiles.
   */
  private syncCostAssumptionsMetricRefs(): void {
    this.eroiComponentsByMetricId.forEach((component, metricId) => {
      this._tvdService.updateComponentData(metricId as eroiIds, component);
    });
  }

  // ──Guard evaluation helpers ───────────────────────────────────────────

  /**
   * Returns the current boolean context snapshot for all guard flags.
   * `isAggregateGhgVisible` is a compound flag that encodes the OR condition
   * `isMultipleRegionsSelected || !isRegionEU` so that the singular GHG tile
   * can be expressed with simple requireAllTrue/requireAllFalse guards.
   * EU is the only region excluded (it uses the composite GHG breakdown instead).
   */
  getGuardContext(): Record<TvdGuardFlag, boolean> {
    return {
      isPestDivision: this.isPestDivision,
      enableTVDForAPInstitutional: this._featureFlags.enableTVDForAPInstitutional,
      enableTVDForLAInstitutional: this._featureFlags.enableTVDForLAInstitutional,
      enableTVDForIMEAInstitutional: this._featureFlags.enableTVDAndSPForIMEAInstitutional,
      isRegionEU: this.isRegionEU,
      isAPInstitutional: this.isAPInstitutional,
      isLAInstitutional: this.isLAInstitutional,
      isIMEAInstitutional: this.isIMEAInstitutional,
      isMultipleRegionsSelected: this.isMultipleRegionsSelected,
      isAggregateGhgVisible: this.isMultipleRegionsSelected || !this.isRegionEU,
      isAPOrLAOrIMEAInstitutionalEnabled:
        (this.isAPInstitutional && this._featureFlags.enableTVDForAPInstitutional) ||
        (this.isLAInstitutional && this._featureFlags.enableTVDForLAInstitutional) ||
        (this.isIMEAInstitutional && this._featureFlags.enableTVDAndSPForIMEAInstitutional)
    };
  }

  /**
   * Returns true if all conditions in the guard pass for the provided context.
   * When `ctx` is omitted a fresh context snapshot is computed.
   */
  passesGuard(guard: Guard | undefined, ctx: Record<TvdGuardFlag, boolean> = this.getGuardContext()): boolean {
    if (!guard) return true;
    if (guard.requireAllTrue?.some(flag => !ctx[flag])) return false;
    if (guard.requireAllFalse?.some(flag => ctx[flag])) return false;
    return true;
  }

  /** Returns true when the tile's guard passes, it has data, and its program key is active. */
  isTileVisible(tile: EroiTileConfig): boolean {
    if (!this.passesGuard(tile.guard)) return false;
    if (!this.getProgramKeys(this.tvdProgramScope).includes(tile.programKey)) return false;
    return !this.isDataZeroOrEmpty(this.getDataByMetricId(tile.metricId));
  }

  /** Returns true when the composite tile's guard passes and at least one child has data. */
  isCompositeVisible(composite: CompositeTileConfig): boolean {
    if (!this.passesGuard(composite.guard)) return false;
    return !this.isSubComponentDataZeroOrEmpty(composite.id);
  }

  /** Returns the subset of a composite's children that have data and an active program key. */
  visibleCompositeChildren(composite: CompositeTileConfig): EroiTileConfig[] {
    return composite.children.filter(child =>
      !this.isDataZeroOrEmpty(this.getDataByMetricId(child.metricId)) &&
      this.getProgramKeys(this.tvdProgramScope).includes(child.programKey)
    );
  }

  //Config-driven visible tile accessors (used by refreshed UI template) ─

  visibleBusinessOutcomesTiles(): EroiTileConfig[] {
    return BusinessOutcomesTilesConfig.filter(tile => this.isTileVisible(tile));
  }

  visibleOperationalPerformanceTiles(): EroiTileConfig[] {
    return OperationalPerformanceTilesConfig.filter(tile => this.isTileVisible(tile));
  }

  visibleOperationalPerformanceCompositeTiles(): CompositeTileConfig[] {
    return OperationalPerformanceCompositeTilesConfig.filter(composite => this.isCompositeVisible(composite));
  }

  visibleEnvironmentalImpactTiles(): EroiTileConfig[] {
    return EnvironmentalImpactTilesConfig.filter(tile => this.isTileVisible(tile));
  }

  visibleEnvironmentalImpactCompositeTiles(): CompositeTileConfig[] {
    return EnvironmentalImpactCompositeTilesConfig.filter(composite => this.isCompositeVisible(composite));
  }

  limitProgramTabs() {
    this.availableProgramTabs =  [availablePrograms.ALL];
  }

  getMetricsFromSessionStorage() {
    let selectedItemsDictSession = new Map<string, [data: tvdData, isSelected: boolean, isAddedToPres: boolean]>(JSON.parse(sessionStorage.getItem("selectedItemsDictSession") || '[]'));
    if (selectedItemsDictSession.size) {
      this.selectedItemsDict = selectedItemsDictSession;
    }
    return selectedItemsDictSession.size;
  }

  setMetricsInsessionStorage() {
    sessionStorage.setItem("selectedItemsDictSession", JSON.stringify(Array.from(this.selectedItemsDict.entries())));
  }

  // TODO: This function is being called in places. We should consider whether it's necessary to call it in all those places or if we can optimize it by only calling it when necessary, such as when the user makes a selection or when the TVD data is updated, instead of calling it on every change detection cycle.
  // TODO: Rename to mapApiDataToUi
  private _parseData(groupID: string = tvdGroupIds.DEFAULT) {
    if(this.filters?.division != "Pest") {
      this._parseProductTvdData(groupID);
    } else {
      this._parseServicesTvdData(tvdGroupIds.PEST);
    }
  }


  // TODO: Rename to mapApiProductDataToUi
  private _parseProductTvdData(groupID: string = tvdGroupIds.DEFAULT) {
    let indvMetrics = this.tvdResponse.find(x => x.id == groupID)?.indvMetrics;
    let summMetrics = this.tvdResponse.find(x => x.id == groupID)?.summMetrics;

    // TODO: This code affects the Cost Assumptions preview. Need to figure out why.
    if(indvMetrics && summMetrics) {
      // Assuming eroiIds contains appropriate keys for each metric
      this.waterData = this.extractMetricData(indvMetrics, eroiIds.WATER);
      this.energyData = this.extractMetricData(indvMetrics, eroiIds.ENERGY);
      this.productivityData = this.extractMetricData(indvMetrics, eroiIds.PRODUCTIVITY);
      this.greenhouseGasData = this.extractMetricData(indvMetrics, eroiIds.GREENHOUSEGASES);
      this.greenhouseGasEnergyData = this.extractMetricData(indvMetrics, eroiIds.GREENHOUSEGASES_ENERGY);
      this.greenhouseGasTransportData = this.extractMetricData(indvMetrics, eroiIds.GREENHOUSEGASES_TRANSPORT);
      this.greenhouseGasPackagingData = this.extractMetricData(indvMetrics, eroiIds.GREENHOUSEGASES_PACKAGING);
      this.plasticWasteData = this.extractMetricData(indvMetrics, eroiIds.PLASTIC);
      this.totalSales = summMetrics.find(x => x.id == eroiIds.TOTALSALES)?.valueContribution ?? '0';

      let replacementCost = this.extractMetricData(indvMetrics, "replacement").monetaryContribution;
      let repairCost = this.extractMetricData(indvMetrics, "repair").monetaryContribution;
      // Combine replacement and repair costs for Assets metric
      const safeParse = (val: string | undefined) => Number((val ?? '0').replace(/,/g, '')) || 0;
      let combinedAssets = safeParse(replacementCost) + safeParse(repairCost);
      this.assetsData = { monetaryContribution: combinedAssets.toLocaleString('en-US'), valueContribution: '0', modifiedValueContribution: '0' };

      this.cardboardWasteData = this.extractMetricData(indvMetrics, eroiIds.CARDBOARD);
      this.employeeSafetyData = this.extractMetricData(indvMetrics, eroiIds.EMPLOYEESAFETY);
      this.portfolioData = this.extractMetricData(indvMetrics, eroiIds.PORTFOLIO);
      this.costAvoidData =  this.extractMetricData(indvMetrics, eroiIds.COSTAVOIDANCE);
    }
  }

  // TODO: Rename to mapApiPestDataToUi
  private _parseServicesTvdData(groupID: string = tvdGroupIds.PEST) {
    let indvMetrics = this.tvdResponse.find(x => x.id == groupID)?.indvMetrics;
    let summMetrics = this.tvdResponse.find(x => x.id == groupID)?.summMetrics;

    // TODO: This code affects the Cost Assumptions preview. Need to figure out why.
    if(indvMetrics && summMetrics) {
      this.totalServices = summMetrics.find(x => x.id == eroiIds.TOTALSERVICES)?.valueContribution ?? '0';

      this.noChargeAdditionalServicesData = this.extractMetricData(indvMetrics, eroiIds.NOCHARGEADDITIONALSERVICES);
      this.generalContractorCostAvoidanceData = this.extractMetricData(indvMetrics, eroiIds.GENERALCONTRACTORCOSTAVOIDANCE);
      this.setupServicesData = this.extractMetricData(indvMetrics, eroiIds.SETUPSERVICES);
      this.pestPreventionFindingsData = this.extractMetricData(indvMetrics, eroiIds.PESTPREVENTIONFINDINGS);
      this.roomNightsSavedData = this.extractMetricData(indvMetrics, eroiIds.ROOMNIGHTSSAVED);
      this.furniturePreservationAssetsData = this.extractMetricData(indvMetrics, eroiIds.FURNITUREPRESERVATIONASSETS);
      this.furniturePreservationEnergyData = this.extractMetricData(indvMetrics, eroiIds.FURNITUREPRESERVATIONENERGY);
    }
  }

  sendData() {
    this._tvdService.setProgramData(this.tvdProgramScope);
    this._tvdService.setTvdMetricData(this.selectedMetrics);
  }


  //only for available eROI metrics
  // TODO : Needs refactoring. The selected items should be determined on what is returned in the TVD response.
  // If a new metric is introduced/existing metric is removed, setting a list like this is prone to bugs
  private _setSelectedItemsDict() {
    const configMap = {
      pestDivision: {
        "no_charge_additional_services": this.noChargeAdditionalServicesData,
        "general_contractor_cost_avoidance": this.generalContractorCostAvoidanceData,
        "setup_services": this.setupServicesData,
        "pest_prevention_findings": this.pestPreventionFindingsData,
        "room_nights_saved": this.roomNightsSavedData,
        "furniture_preservation_assets": this.furniturePreservationAssetsData,
        "furniture_preservation_energy": this.furniturePreservationEnergyData
      },
      nonPestDivision: {
        "water": this.waterData,
        "energy": this.energyData,
        "productivity": this.productivityData,
        "greenhouse_gases": this.greenhouseGasData,
        "greenhouse_gases_energy": this.greenhouseGasEnergyData,
        "greenhouse_gases_transport": this.greenhouseGasTransportData,
        "greenhouse_gases_packaging": this.greenhouseGasPackagingData,
        "plastic_waste": this.plasticWasteData,
        "assets": this.assetsData,
        "cardboard_waste": this.cardboardWasteData,
        "employee_safety": this.employeeSafetyData,
        "portfolio_reduction": this.portfolioData, // TODO: Is portfolio_reduction still a valid metric?
        "cost_avoidance": this.costAvoidData
      }
    };

    // Get the correct config based on division type
    const config = this.isPestDivision ? configMap.pestDivision : configMap.nonPestDivision;

    // Snapshot existing selection state so it can be preserved across data refreshes
    const previousDict = new Map(this.selectedItemsDict);
    this.selectedItemsDict.clear();

    // Populate the map with only entries that have valid (non-zero/non-empty) data.
    // Preserve isSelected / isAddedToPres flags for keys that already existed so
    // that in-progress or committed selections survive a data refresh.
    Object.entries(config).forEach(([key, data]) => {
      if (!this.isDataZeroOrEmpty(data)) {
        const existing = previousDict.get(key);
        this.selectedItemsDict.set(key, [data, existing?.[1] ?? false, existing?.[2] ?? false]);
      }
    });

    // Also include custom tiles so they are never wiped out during a rebuild and
    // are always available to addToPresentation (including the 0-tiles-selected path).
    // Preserve existing selection state; tiles new to this rebuild default to unselected.
    if (this.customTilesDict) {
      for (const [title, tile] of this.customTilesDict) {
        const existing = previousDict.get(title);
        const tileData: tvdData = {
          monetaryContribution: tile.cost,
          valueContribution: tile.metricValue.toString()
        };
        this.selectedItemsDict.set(title, [tileData, existing?.[1] ?? false, existing?.[2] ?? false]);
      }
    }
  }

  toggleMakingSelectionsState() {
    this.makingSelectionsState = !this.makingSelectionsState;

    if (this.makingSelectionsState) {
      // Snapshot the current isAddedToPres state so Cancel can restore it even if
      // clearSelectedTilesForPresentation() was called during a preview mid-session.
      this._isAddedToPresSnapshot.clear();
      for (const [key, value] of this.selectedItemsDict) {
        this._isAddedToPresSnapshot.set(key, value[2]);
      }
      this.refreshValuePillarSelectionStates();
    }
  }

  /**
   * Reverts any pending selection changes by setting the isSelected value back to match the isAddedToPres value for each metric in selectedItemsDict.
   * If a tile was selected but not yet added to the presentation, this will unselect it.
   * If a tile was unselected but is already added to the presentation, this will reselect it.
   */
  cancelMakingSelectionsState() {
    // Restore isAddedToPres from the snapshot taken when the panel opened, so that a
    // clearSelectedTilesForPresentation() call made during preview does not become permanent
    // when the user cancels instead of confirming with Done.
    for (const [key, value] of this.selectedItemsDict) {
      const snapshotIsAdded = this._isAddedToPresSnapshot.get(key) ?? value[2];
      this.selectedItemsDict.set(key, [value[0], snapshotIsAdded, snapshotIsAdded]);
    }
    // Re-sync the service to the restored state.
    this._tvdService.updateSelectionsPayload(this.selectedItemsDict);
    this.makingSelectionsState = false;
  }

  toggleSelectionConfirmationState() {
    this.selectionConfirmationState = !this.selectionConfirmationState;
  }

  toggleSelectionsPreviewState() {
    this.selectionsPreviewState = !this.selectionsPreviewState;
  }

  addCustomTile() {
    const maxSave = this.maxCustomTiles;

    // don't attempt to add if over max
    if ((this.customTilesDict?.size ?? 0) < maxSave) {
      this.hideCover();
      this.showCover();
      this.showCustEntryTilePanel();
      this.mixpanelService.track('Add Custom Tile Clicked', {
        'Division': this.filters?.division,
        'DataView': this.filters?.dataView
       });
      return true;

    } else {
      window.alert('Only ' + (maxSave) + ' custom tiles can be saved.');
      return false;
    }
  }

  hideCustEditTilePanel() {
    // hide the entry popup box
    this.custTileEditVisible = false;
  }

  hideCustEntryTilePanel() {

    // only show the section for custom tiles if there are any
    this.hideCustTileSection();
    if(this.customTilesDict?.size ?? 0 > 0){
      this.showCustTileSection();
    }

    // hide the entry popup box
    this.custTileEntryVisible = false;
  }

  /**
   * After a custom tile is added, we need to check if the number of tiles in the value pillar
   * exceeds the max allowed in presentation, and if so, show a warning to the user.
   */
  checkValuePillarTileCountAfterCustomTile(newCustomTile: custTile): void {
    let countOfMetrics = this.countTotalMetricsPerValuePillar(newCustomTile.valuePillar);
    if (countOfMetrics > this.maxTilesPerValuePillar) {
      window.alert(`Only ${this.maxTilesPerValuePillar} metrics can be displayed in the Powerpoint per value pillar.\n\nMost recently added metric will not be shown in the Powerpoint but is included in the total.\n\nUse the "Add to Presentation" button to adjust which metrics will be visible.`);
    }
  }

  /**
   * Checks if the number of selected metrics for a given value pillar exceeds the maximum allowed.
   * If so, show a warning and reset the selection of the lastly added metric(s). ALso set a flag
   * so that the metrics and custom tiles child components can disable their selection toggle.
   * @param metricTitle
   * @param valuePillar
   */
  checkTotalTilesSelected(metricTitle:string, valuePillar: TvdValuePillar): void {
    let countOfMetricsSelectedForValuePillar = this.countSelectedMetricsPerValuePillar(valuePillar);
    if(countOfMetricsSelectedForValuePillar > this.maxTilesPerValuePillar){
      // Since the "Add Entire Value Pillar" button selects all the metrics in the value pillar at once,
      // this method gets triggered for each metric in the value pillar
      // and we only want to show the warning message once,
      // to avoid spamming the user with multiple alerts at once.
      if(this._tvdService.getSelectedMetricsLimit(valuePillar) == false){
        window.alert(`Only ${this.maxTilesPerValuePillar} metrics can be displayed in the Powerpoint per value pillar.\n\nPlease deselect another metric from the same value pillar before selecting this one.`);
      }

      //Reset the metric selected flag to false since the limit has been reached
      const current = this.selectedItemsDict.get(metricTitle);
      if (current) {
        const next = new Map(this.selectedItemsDict);
        next.set(metricTitle, [current[0], false, current[2]]);
        //This triggers the change detection to update the toggle state of the child components in the UI
        this.selectedItemsDict = next;
      }
      this._tvdService.setSelectedMetricsLimit(valuePillar, true);
    }
    else{ //countOfMetricsSelectedForValuePillar <= this.maxTilesPerValuePillar
      // reset the warning flag to allow future warnings if the user exceeds the limit again.
      this._tvdService.setSelectedMetricsLimit(valuePillar, false);
    }
  }


  showCustEntryTilePanel() {
    this.custTileEntryVisible = true;
  }

  hideCustTileSection() {
    this.custTileSectionVisible = false;
  }

  showCustTileSection() {
    this.custTileSectionVisible = true;
  }

  // Hide the cover grey transparency
  hideCover() {
    this.coverVisible = false;
  }

  // Show the cover grey transparency
  showCover() {
    this.coverVisible = true;
  }

  // Clear the custom tiles
  async clearCustomTiles() {
    if (confirm("Are you sure you want to clear all of the custom tiles?" )) {
      if(this.customTilesDict){
        for(let title of this.customTilesDict.keys()){
          this.selectedItemsDict.delete(title);
        }
        this.customTilesDict.clear();

        this._tvdService.updateAddedMetrics([]);
        this.hideCustTileSection();
        this._tvdService.updateCustomTileDict(this.customTilesDict!);

        // Update the TVD Overall metric after clearing custom tiles
        if (this._tvdService.hasActiveCostAdjustments()) {
          const formData = this._tvdService.getFormData();
          const assumptions = this._tvdCostAssumptionsService.getPestPayloadFromFormData(formData);
          await this._tvdService.refreshTVDDataWithAssumptions(assumptions);
        }
      }
    }
    return false;
  }

  /**
   * Commits the current tile selections to the presentation payload.
   *
   * When no tiles are explicitly selected, auto-selects up to `maxTilesPerValuePillar`
   * tiles per value pillar (standard metrics first, then custom tiles) and shows a
   * contextual confirmation message. When tiles are already selected, those selections
   * are respected as-is.
   */
  addToPresentation() {
    let didAutoSelect = false;
    let pillarLimitReached = false;
    if (this.countSelectedItems() === 0) {
      didAutoSelect = true;
      pillarLimitReached = this.autoSelectMaximumItems();
    }

    // Reset representation of metrics selected that used for mixpanel events.
    this.selectedMetrics = [];

    // push the added items into the model to be sent to the presentation download
    for(let [key, value] of this.selectedItemsDict){
      // if the metric item is selected
      if(value[1]==true){
        this.selectedItemsDict.set(key, [value[0], value[1], true]);
        this.selectedMetrics.push(key);
        this.selectedMetrics = this.selectedMetrics.map(key => key.charAt(0).toUpperCase() + key.slice(1));
      }
      else { //metric is not selected
        this.selectedItemsDict.set(key, [value[0], value[1], false]);
      }
    }

    this._tvdService.updateSelectionsPayload(this.selectedItemsDict);

    this.mixpanelService.track('Add to Presentation Clicked', {
      'Division': this.filters?.division,
      'DataView': this.filters?.dataView,
      "countriesSelected": this.filters?.countries,
      "customerNumberSelected": this.filters?.accts,
      "H5s": this.filters?.topLevelContractGroup,
      "H7s": this.filters?.secondLevelContractFilter,
      "periodSelected": this.filters?.period,
      "Custom Date Range": this.filters?.dateRange,
      "regionSelected": this.filters?.regions,
      "templateSelected": this.filters?.presentationType ? this.filters.presentationType : 'All',
      "templateTranslationSelected": this.filters?.translation ? this.filters.translation : 'English',
      "Page Name": "Total Value Delivered",
      "TVD Metric Added": this.selectedMetrics,
      "Program": this.tvdProgramScope,
      "Total Value Delivered $ amount " :this.tvdOverallData,
      "Total Sales":this.totalSales,
    });

    alert(didAutoSelect
      ? pillarLimitReached
        ? `No metrics selected manually in the Add to Presentation experience.\n\nUp to ${this.maxTilesPerValuePillar} metrics have been automatically selected for each Value Pillar as the maximum limit per pillar has been reached.\n\nPlease review your selections before downloading the PowerPoint.`
        : "No metrics selected manually in the Add to Presentation experience.\n\nAll metrics will be added to the PowerPoint."
      : this.countSelectedItems() + " Items Added Successfully");
    this.updateIsMetricsSelectedFunc();
    this.setMetricsInsessionStorage();

    // Toggle the states
    this.toggleSelectionConfirmationState();
    this.toggleMakingSelectionsState();
  }

  /**
   * Auto-selects tiles per value pillar up to `maxTilesPerValuePillar` when no explicit
   * selection exists. Standard metrics are iterated before custom tiles within each pillar.
   * Mutates `selectedItemsDict` in place.
   *
   * @returns `true` if any pillar's available tile count reached the per-pillar limit
   *          during auto-selection (used to show an appropriate confirmation message).
   */
  autoSelectMaximumItems(): boolean {
    let pillarLimitReached = false;
    const pillars = [TvdValuePillar.BusinessOutcomes, TvdValuePillar.OperationalPerformance, TvdValuePillar.EnvironmentalImpact];
    // For each value pillar, respect the maximum tiles that can be added.
    // Start adding standard metrics first followed by custom tiles.
    for (const pillar of pillars) {
      const standardMetricKeys = this.getAvailableMetricsByValuePillar(pillar)
        .map(m => m.metricId)
        .filter((id): id is string => !!id);

      const customTileKeys = this.GetCustomTilesGivenValuePillar(pillar).map(t => t.title);

      let countSelected = 0;
      for (const key of [...standardMetricKeys, ...customTileKeys]) {
        if (countSelected >= this.maxTilesPerValuePillar) {
          pillarLimitReached = true;
          break;
        }

        const entry = this.selectedItemsDict.get(key);
        if (entry) {
          this.selectedItemsDict.set(key, [entry[0], true, entry[2]]);
          countSelected++;
        }
      }
    }

    return pillarLimitReached;
  }

  updateIsMetricsSelectedFunc() {
    //Set the isMetricSelected boolean value
    this.isMetricSelected = false;
    for (let [key, value] of this.selectedItemsDict) {
      if (value[2]) {
        this.isMetricSelected = true;
      }
    }
  }

  getProgramKeys(program: string): string[] {
    let keys = this._eroiService.getProgramKeys(program);
    return keys;
  }

  sharedToggleBoxSelection(array: EroiMetricComponent[], selectedFlag: boolean) {
    array.forEach(element => {
      if (element != undefined && element.data != undefined) {
        if (element.selectedItem == selectedFlag) {
          element.toggleSelectItem(element.title); // TODO: Consider using EROID instead of title for better reliability in case of title changes or duplicates
        }
      }
    });
  }

  /**
   * If all metrics in the Value Pillar are selected, return true.
   * @param children
   * @returns
   */
  public areAllValuePillarMetricsSelected(children: EroiMetricComponent[]): boolean {
    for (const component of children) {
      //if any metric is not selected, return false
      if (component && component.selectedItem === false) {
        return false;
      }
    }
    return true;
  }

  /**
   * If all custom metrics in the Value Pillar are selected, return true.
   * @param valuePillar
   * @returns
   */
  // TODO: Consolidate custom-tile state into one source of truth.
  // Right now state is duplicated across:
  // - this.customTilesDict / this._tvdService.custTileDict (active tile data)
  // - this.selectedItemsDict / this._tvdService.selectedItemsDict (selection + added-to-presentation flags)
  // - this._tvdService.custTileObjectList (component instances + local selectedItem values)
  // These structures get out of sync during add/edit/clear flows, making it unclear which one is authoritative.
  // Centralize reads/writes in a single store and derive UI/component state from that store only.
  public areAllCustomMetricSelectedForValuePillar( valuePillar : string | TvdValuePillar): boolean {
    // Use active custom tile data (dict + selection map) to avoid stale component instances
    // from affecting pillar state after add/remove/clear flows.
    const customTilesInPillar = this.GetCustomTilesGivenValuePillar(valuePillar);
    for (const customTile of customTilesInPillar) {
      const isSelected = this.selectedItemsDict.get(customTile.title)?.[1];
      if (isSelected !== true) {
        return false;
      }
    }
    return true;
  }

  /**
   * Recomputes pillar selection flags based on current metric and custom tile selections.
   * Call whenever selection state changes (toggle, tile add/remove, mode transitions) to prevent stale UI.
   */
  private refreshValuePillarSelectionStates(): void {
    const updatePillarState = (valuePillar: TvdValuePillar): boolean => {
      const availableMetrics = this.getAvailableMetricsByValuePillar(valuePillar);
      const availableCount = this.countAvailableValuePillarMetrics(valuePillar);

      if (availableCount === 0) {
        return false;
      }

      return this.areAllValuePillarMetricsSelected(availableMetrics)
        && this.areAllCustomMetricSelectedForValuePillar(valuePillar);
    };

    this.businessOutcomesSelected = updatePillarState(TvdValuePillar.BusinessOutcomes);
    this.operationalPerformanceSelected = updatePillarState(TvdValuePillar.OperationalPerformance);
    this.environmentalImpactSelected = updatePillarState(TvdValuePillar.EnvironmentalImpact);
  }

  /**
   * Get a list of custom tiles that are a part of the given Value Pillar.
   * @param valuePillar
   * @returns
   */
  public GetCustomTilesGivenValuePillar(valuePillar: string | TvdValuePillar) : custTile[]{
    let customMetricsInPillar :custTile[] = [];
    if(this.customTilesDict){
      for (const [metricTitle, customTile] of this.customTilesDict.entries()) {
        if(customTile.valuePillar == valuePillar as TvdValuePillar){
          customMetricsInPillar.push(customTile);
        }
      }
    }
    return customMetricsInPillar;
  }

  toggleBusinessOutcomesBox() {
    let selectedFlag : boolean = this.businessOutcomesSelected;
    let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.BusinessOutcomes);
    this.sharedToggleBoxSelection(availableMetrics, selectedFlag);
    this.ToggleSelectionOfCustomTilesByValuePillar(TvdValuePillar.BusinessOutcomes, selectedFlag);
  }

  toggleOperationalPerformanceBox(){
    let selectedFlag : boolean = this.operationalPerformanceSelected;
    let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.OperationalPerformance);
    this.sharedToggleBoxSelection(availableMetrics, selectedFlag);
    this.ToggleSelectionOfCustomTilesByValuePillar(TvdValuePillar.OperationalPerformance, selectedFlag);
  }

  toggleEnvironmentalImpactBox() {
    let selectedFlag : boolean = this.environmentalImpactSelected;
    let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.EnvironmentalImpact);
    this.sharedToggleBoxSelection(availableMetrics, selectedFlag);
    this.ToggleSelectionOfCustomTilesByValuePillar(TvdValuePillar.EnvironmentalImpact, selectedFlag);
  }


  public getAvailableMetricsByValuePillar(valuePillar: string | TvdValuePillar) : EroiMetricComponent[]{
    let availableMetrics : EroiMetricComponent[] = [];
    if(valuePillar == TvdValuePillar.BusinessOutcomes){
      availableMetrics = this.businessOutcomesChildren.filter(component => component?.data && !this.isDataZeroOrEmpty(component.data) && component.data.monetaryContribution !="");
    } else if (valuePillar == TvdValuePillar.OperationalPerformance){
      availableMetrics = this.operationalPerformanceChildren.filter(component => component?.data && !this.isDataZeroOrEmpty(component.data) && component.data.monetaryContribution !="");
    } else if (valuePillar == TvdValuePillar.EnvironmentalImpact){
      availableMetrics = this.environmentalImpactChildren.filter(component => component?.data && !this.isDataZeroOrEmpty(component.data) && component.data.monetaryContribution !="");
    }
    return availableMetrics;
  }

  private ToggleSelectionOfCustomTilesByValuePillar(valuePillar : string | TvdValuePillar, valuePillarSelected: boolean){
    if(this._tvdService.custTileObjectList != undefined){
      // Filter to currently active custom tiles only so removed tiles do not affect selection toggles.
      const activeTileTitles = new Set(this.GetCustomTilesGivenValuePillar(valuePillar).map(tile => tile.title));
      let valuePillarCustomTiles = this._tvdService.custTileObjectList.filter(item =>
        item.custTileValuePillar == valuePillar && !!item.custTileTitle && activeTileTitles.has(item.custTileTitle)
      )
      for(let i = 0; i<valuePillarCustomTiles.length; i++){
        if(valuePillarCustomTiles[i].selectedItem == valuePillarSelected){
          valuePillarCustomTiles[i].toggleSelectItem(valuePillarCustomTiles[i].custTileTitle);
        }
      }
    }
  }

  isDataZeroOrEmpty(tvdData: tvdData): boolean {
    const monetary = Number(tvdData.monetaryContribution?.replace(/,/g, '') || 0);
    const value = Number(tvdData.valueContribution || 0);
    return monetary === 0 && value === 0;
  }

  isSubComponentDataZeroOrEmpty(parentComponentId: string): boolean {
    const composite = compositeTilesConfig.find(
      c => c.composite_tile_id === parentComponentId
    );

    if (!composite) {
      console.warn(`Composite tile not found for: ${parentComponentId}`);
      return true;
    }

    for (const childId of composite.subcomponents) {
      const data = this.getDataByMetricId(childId);

      if (!this.isDataZeroOrEmpty(data)) {
        return false;
      }
    }

    return true;
  }

  getSubComponentsData(parentComponentId: string): tvdData[] {
    const composite = compositeTilesConfig.find(
      c => c.composite_tile_id === parentComponentId
    );

    if (!composite) {
      console.warn(`Composite tile not found for: ${parentComponentId}`);
      return [];
    }

    return composite.subcomponents.map(childId => this.getDataByMetricId(childId));
  }

  //#region UI/UX functions

  /**
   * Triggered when user selects an individual metric in the UI.
   * If all metrics in the Value Pillar are selected, switch the [businessOutcomesSelected | operationalPerformanceSelected | environmentalImpactSelected] to true.
   * @param bucketType
   */
  public automaticBucketToggle(bucketType: TvdValuePillar): void {
    if (bucketType === TvdValuePillar.BusinessOutcomes) {
      //filter all possible metrics only to the ones that have data and are present for the customer
      let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.BusinessOutcomes);
      if (this.areAllValuePillarMetricsSelected(availableMetrics) && this.areAllCustomMetricSelectedForValuePillar(TvdValuePillar.BusinessOutcomes)) {
        this.businessOutcomesSelected = true;
      }
    } else if (bucketType === TvdValuePillar.OperationalPerformance) {
      //filter all possible metrics only to the ones that have data and are present for the customer
      let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.OperationalPerformance);
      if (this.areAllValuePillarMetricsSelected(availableMetrics) && this.areAllCustomMetricSelectedForValuePillar(TvdValuePillar.OperationalPerformance)) {
        this.operationalPerformanceSelected = true;
      }
    } else if (bucketType === TvdValuePillar.EnvironmentalImpact) {
      //filter all possible metrics only to the ones that have data and are present for the customer
      let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.EnvironmentalImpact);
      if (this.areAllValuePillarMetricsSelected(availableMetrics) && this.areAllCustomMetricSelectedForValuePillar(TvdValuePillar.EnvironmentalImpact)) {
        this.environmentalImpactSelected = true;
      }
    }
  }

  /**
   * Triggered when user deselects an individual metric in the UI.
   * If any metric in the Value Pillar is deselected, switch the [businessOutcomesSelected | operationalPerformanceSelected | environmentalImpactSelected] to false.
   * @param bucketType
   */
  public automaticBucketToggleOff(bucketType: TvdValuePillar): void {
    if (bucketType === TvdValuePillar.BusinessOutcomes && this.businessOutcomesSelected) {
      //filter all possible metrics only to the ones that have data and are present for the customer
      let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.BusinessOutcomes);
      if (!this.areAllValuePillarMetricsSelected(availableMetrics) || !this.areAllCustomMetricSelectedForValuePillar(TvdValuePillar.BusinessOutcomes)) {
        this.businessOutcomesSelected = false;
      }
    } else if (bucketType === TvdValuePillar.OperationalPerformance && this.operationalPerformanceSelected) {
      //filter all possible metrics only to the ones that have data and are present for the customer
      let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.OperationalPerformance);
      if (!this.areAllValuePillarMetricsSelected(availableMetrics) || !this.areAllCustomMetricSelectedForValuePillar(TvdValuePillar.OperationalPerformance)) {
        this.operationalPerformanceSelected = false;
      }
    } else if (bucketType === TvdValuePillar.EnvironmentalImpact && this.environmentalImpactSelected) {
      //filter all possible metrics only to the ones that have data and are present for the customer
      let availableMetrics = this.getAvailableMetricsByValuePillar(TvdValuePillar.EnvironmentalImpact);
      if (!this.areAllValuePillarMetricsSelected(availableMetrics) || !this.areAllCustomMetricSelectedForValuePillar(TvdValuePillar.EnvironmentalImpact)) {
        this.environmentalImpactSelected = false;
      }
    }
  }


  private countSelectedMetricsPerValuePillar(valuePillar: TvdValuePillar): number {
    let countTilesSelected = 0;
    // Count the number of custom tiles selected for the Value Pillar and add to countTilesSelected
    const customTilesInPillar = this.GetCustomTilesGivenValuePillar(valuePillar);
    for (const customTile of customTilesInPillar) {
      const isSelected = this.selectedItemsDict.get(customTile.title)?.[1];
      if (isSelected == true) {
        countTilesSelected++;
      }
    }

    // Count the number of automatic metrics selected for the Value Pillar and add to countTilesSelected
    let availableAutomaticMetrics = this.getAvailableMetricsByValuePillar(valuePillar);
    for (const metric of availableAutomaticMetrics) {
      if (metric.selectedItem == true) {
        countTilesSelected++;
      }
    }

    return countTilesSelected;
  }

  private countTotalMetricsPerValuePillar(valuePillar: TvdValuePillar): number {
    let countOfTiles = 0;
    const customTilesInPillar = this.GetCustomTilesGivenValuePillar(valuePillar);
    countOfTiles += customTilesInPillar.length;

    // Count the number of automatic metrics selected for the Value Pillar and add to countTilesSelected
    let availableAutomaticMetrics = this.getAvailableMetricsByValuePillar(valuePillar);
    countOfTiles += availableAutomaticMetrics.length;

    return countOfTiles;
  }

  countSelectedItems(): number {
    return this._tvdService.countSelectedItems(this.selectedItemsDict);
  }

  countAvailableValuePillarMetrics(valuePillar: TvdValuePillar | string): number {
    switch(valuePillar){
      case TvdValuePillar.BusinessOutcomes:
        return this.countAvailableBusinessOutcomes();
      case TvdValuePillar.OperationalPerformance:
        return this.countAvailableOperationalPerformance();
      case TvdValuePillar.EnvironmentalImpact:
        return this.countAvailableEnvironmentalImpact();
      default:
        return 0;
    }
  }

  countAvailableBusinessOutcomes(): number {
    // Derive count directly from config to avoid
    // dependency on the component registry before ngAfterViewInit.
    return BusinessOutcomesTilesConfig.filter(tile => this.isTileVisible(tile)).length
      + this.GetCustomTilesGivenValuePillar(TvdValuePillar.BusinessOutcomes).length;
  }

  countAvailableOperationalPerformance(): number {
    // Derive count directly from config.
    const individualCount = OperationalPerformanceTilesConfig.filter(tile => this.isTileVisible(tile)).length;
    const compositeChildCount = OperationalPerformanceCompositeTilesConfig
      .filter(composite => this.isCompositeVisible(composite))
      .reduce((sum, composite) => sum + this.visibleCompositeChildren(composite).length, 0);
    return individualCount + compositeChildCount
      + this.GetCustomTilesGivenValuePillar(TvdValuePillar.OperationalPerformance).length;
  }

  countAvailableEnvironmentalImpact(): number {
    // Derive count directly from config.
    const individualCount = EnvironmentalImpactTilesConfig.filter(tile => this.isTileVisible(tile)).length;
    const compositeChildCount = EnvironmentalImpactCompositeTilesConfig
      .filter(composite => this.isCompositeVisible(composite))
      .reduce((sum, composite) => sum + this.visibleCompositeChildren(composite).length, 0);
    return individualCount + compositeChildCount
      + this.GetCustomTilesGivenValuePillar(TvdValuePillar.EnvironmentalImpact).length;
  }

  getMetricsForPillar(valuePillar: TvdValuePillar) {
    if (!this.tvdModel || !this.filters) return [];

    return this._tvdService.getMetricsForValuePillar(
      this.tvdModel.tvdResponse,
      valuePillar,
      this.filters.division,
      this.tvdProgramScope
    );
  }

  hasMetricsForPillar(p: TvdValuePillar) {
    // Include both predefined metrics AND custom tiles so View Details is available
    // when either type of tile exists for the value pillar
    return this.countAvailableValuePillarMetrics(p) > 0;
  }

  // TODO: This is a simplified version. Verify that the code in the original code is consistent with getMetricsForPillar or the underlying functionn called by it particularly rules on the division, regions, assets, etc.
  countMetricsForPillar(p: TvdValuePillar) {
    return this.getMetricsForPillar(p).filter(metric => {
      const value =
        metric.modifiedMonetaryContribution ??
        metric.monetaryContribution ??
        '0';

      return Number(value.replace(/,/g, '') || 0) > 0;
    }).length;
  }

  //adjusts UI view on which eroi metrics are shown
  changeScope(programName: string) {
    this.tvdProgramScope = programName;
    this.makingSelectionsState = false;

    //call _parseData which will change the individual tvdData objects,
    //but will not re-set the selectedItemsDict since it is just for display purposes only
    switch (programName) {
      case availablePrograms.ALL:
        this._parseData();
        this.getMetricsFromSessionStorage();
        break;
      case availablePrograms.AQUANOMIC:
        this._parseData(tvdGroupIds.AQUANOMIC);
        break;
      case availablePrograms.SMARTPOWER:
        this._parseData(tvdGroupIds.SMARTPOWER);
        break;
      default:
        break;
    }

    if (this.tvdModel && this.tvdResponse) {
      this.tvdModel = {
        ...this.tvdModel,
        // TODO: It's a  weird that we have to do this, but it seems that the tvdModel is being mutated somewhere else in the code, which is causing issues with change detection. By creating a new object with a deep copy of the tvdResponse, we can ensure that the tvdModel is not mutated and that change detection works as expected.
        // Use JSON stringify/parse to create a deep copy of the tvdResponse and assign it to the tvdModel. This ensures that any changes made to the tvdModel do not affect the original tvdResponse.
        tvdResponse: JSON.parse(JSON.stringify(this.tvdResponse))
      };
    }

    this._tvdService.setTvdProgramSelected(programName);
    this._tvdService.recalculateTvdTotals(
      this.tvdModel,
      this.customTilesDict,
    );
  }

  //#endregion

  getFilterSubTitle() {
    let subtitle : string;
    const selectedDivision = this.filters?.division;

    subtitle = 'H5: 0 | H7: 0 | Accounts: 0';

    if (this.filters?.topLevelContractGroup != undefined && this.filters.secondLevelContractFilter != undefined && this.filters.accts != undefined) {
      let topLevelContract = `H5: ${this.filters.topLevelContractGroup.length}`;
      let secondLevelContract = `H7: ${this.filters.secondLevelContractFilter.length}`;

      // Change labels if selected Division is Pest
      if(selectedDivision == "Pest") {
        topLevelContract = `Enterprise Names : ${this.filters.topLevelContractGroup.length}`;
        secondLevelContract = `Corporate Reference Names : ${this.filters.secondLevelContractFilter.length}`;;
      }

      let customerLabel = selectedDivision == "Pest" ? `Service Account ID : ${this.filters.accts.length}` : `Accounts: ${this.filters.accts.length}`;

      subtitle = `${topLevelContract} | ${secondLevelContract} |  ${customerLabel}`;
    }

    return subtitle;
  }

  getEntries(): [string, [data: tvdData, isSelected: boolean, isAddedToPres: boolean]][] {
    return Array.from(this.selectedItemsDict?.entries());
  }

  /**
   * Returns the information for a desired metric from a collection of metrics
   * @param metrics collection of metrics being searched upon
   * @param metricId id of the desired metric
   * @returns monetary and value contribution of the desired TVD metric
   */
  private extractMetricData(metrics: any[], metricId: string) {
    const metric = metrics.find(x => x.id == metricId);

    return {
      monetaryContribution: metric?.monetaryContribution,
      modifiedMonetaryContribution: metric?.modifiedMonetaryContribution,
      valueContribution: metric?.valueContribution,
      modifiedValueContribution: metric?.modifiedValueContribution
    };
  }

  openSideDrawer() {
    this.isDrawerOpen = true;

    // Set interaction type context
    this._tvdCostAssumptionsService.setInteractionContext('All Metrics', 'All');

    // Push metrics to mixpanel
    this.mixpanelService.trackEventWithFilters('Opened the Cost Assumptions Side Navbar', this.filters,
      this.pageTitle,
      { 'Interaction Type': 'All Metrics' }
    );
  }

  closeSideDrawer() {
    this.isDrawerOpen = false;
  }

  getModalAction(action:string) {
    // TODO: Requires further impact analysis if this can be safely removed. Original content triggers multiple subscriptions which is causing issues with calculations for productivity.
  }

  toggleRounding() {
    this._uiPreferencesService.setDisplayRoundedValues(!this.displayInRoundedNumber);
  }

  toggleShowEnterpriseTerms() {
    this._tvdService.setShowInEnterpriseTerms(!this.showInEnterpriseTerms);
  }

  getSummaryPillData(metricId: string): tvdData[]{
    const metricGroup = tvdSummaryPillsConfig.find(
      c => c.metricGroupId === metricId
    );

    if(!metricGroup) {
      console.warn(`Metric group not found for: ${metricId}`);
      return [];
    }

    return metricGroup.metricSummaryComponents.map(metricId => this.getDataByMetricId(metricId));
  }

  onValuePillarToggle(expandedValuePillar: TvdValuePillar){
    this.expandedValuePillarId = this.expandedValuePillarId === expandedValuePillar ? null : expandedValuePillar;
  }

  trackByValuePillar(index: number, valuePillar: TvdValuePillar) {
    return valuePillar;
  }

  /**
   * Clears the selected tiles that were added to presentation when triggered from the preview.
   * This allows the user to return to the selection screen with a clean slate after previewing their presentation,
   * without any previously added tiles still showing as selected.
   */
  clearSelectedTilesForPresentation() {
    // Clear the selected items dict of the items that were added to presentation
    for(let [key, value] of this.selectedItemsDict){
      if(value[2]==true){
        this.selectedItemsDict.set(key, [value[0], value[1], false]);
      }
    }
    // Update the TVD Service with the cleared selected items dict
    this._tvdService.updateSelectionsPayload(this.selectedItemsDict);

    // Set all eroi metric items that were added to presentation to unselected in the UI
    this.businessOutcomesChildren.forEach(component => {
      if (component?.data && component.selectedItem) {
        component.toggleSelectItem(component.title); // TODO: Consider using EROID instead of title for better reliability in case of title changes or duplicates
      }
    });
    this.operationalPerformanceChildren.forEach(component => {
      if (component?.data && component.selectedItem) {
        component.toggleSelectItem(component.title); // TODO: Consider using EROID instead of title for better reliability in case of title changes or duplicates
      }
    });
    this.environmentalImpactChildren.forEach(component => {
      if (component?.data && component.selectedItem) {
        component.toggleSelectItem(component.title); // TODO: Consider using EROID instead of title for better reliability in case of title changes or duplicates
      }
    });

    // Since custom tiles are not part of the ref lists update their state seperately
    this._tvdService.custTileObjectList?.forEach(component => {
      if (component?.selectedItem) {
        component.toggleSelectItem(component.custTileTitle); // TODO: Consider using a unique identifier instead of title for better reliability in case of title changes or duplicates
      }
    });

    // Recalculate isMetricSelected since all items are now unselected
    this.updateIsMetricsSelectedFunc();
  }

  getDataByMetricId(metricId: string): tvdData {
    switch (metricId) {
      // Business Outcomes
      case eroiIds.EMPLOYEESAFETY: return this.employeeSafetyData;

      // Operational Performance
      case eroiIds.PRODUCTIVITY: return this.productivityData;
      case eroiIds.ASSETS: return this.assetsData;
      case eroiIds.COSTAVOIDANCE: return this.costAvoidData;
      case eroiIds.PORTFOLIO: return this.portfolioData;
      case eroiIds.NOCHARGEADDITIONALSERVICES:return this.noChargeAdditionalServicesData;
      case eroiIds.GENERALCONTRACTORCOSTAVOIDANCE:return this.generalContractorCostAvoidanceData;
      case eroiIds.SETUPSERVICES:return this.setupServicesData;
      case eroiIds.PESTPREVENTIONFINDINGS:return this.pestPreventionFindingsData;
      case eroiIds.FURNITUREPRESERVATIONASSETS:return this.furniturePreservationAssetsData;
      case eroiIds.ROOMNIGHTSSAVED:return this.roomNightsSavedData;

      // Environmental Impact
      case eroiIds.WATER:return this.waterData;
      case eroiIds.ENERGY:return this.energyData;
      case eroiIds.GREENHOUSEGASES:return this.greenhouseGasData;
      case eroiIds.GREENHOUSEGASES_ENERGY:return this.greenhouseGasEnergyData;
      case eroiIds.GREENHOUSEGASES_TRANSPORT:return this.greenhouseGasTransportData;
      case eroiIds.GREENHOUSEGASES_PACKAGING:return this.greenhouseGasPackagingData;
      case eroiIds.PLASTIC:return this.plasticWasteData;
      case eroiIds.CARDBOARD:return this.cardboardWasteData;
      case eroiIds.FURNITUREPRESERVATIONENERGY:return this.furniturePreservationEnergyData;

      default: throw new Error(`Invalid metricId: ${metricId}`);
    }
  }

  getCurrentGroupId(): string {
    const defaultGroupId = this.filters?.division == Division.Pest ? tvdGroupIds.PEST : tvdGroupIds.DEFAULT;
    if (this.tvdProgramScope === availablePrograms.ALL) {
      return defaultGroupId;
    }

    return TvdProgramToGroupIdMapping.get(this.tvdProgramScope)
      ?? defaultGroupId;
  }
}
